import {
  addDays,
  asIsoDate,
  DRAWING_TYPE_LABELS,
  drawingPriceAt,
  evaluateStopLossAlert,
  formatPrice,
  type CreateDrawingInput,
  type Drawing,
  type DrawingPayload,
  type DrawingType,
  type PositionView,
} from '@stockstop/shared';
import { useCallback, useMemo, useState } from 'react';
import {
  useBars,
  useCreateDrawing,
  useDeleteDrawing,
  useDrawings,
  useLinkDrawing,
  useSyncStopLoss,
  useUnlinkDrawing,
  useUpdateDrawing,
} from '../../api/queries.js';
import { DrawingList } from './DrawingList.js';
import { LinkState } from './LinkState.js';
import { Popover } from '../../components/Popover.js';
import { useChart } from './useChart.js';

/**
 * Chart panel: candles, zoom controls and the drawing tools.
 *
 * New drawings are seeded from the currently visible data — a horizontal line
 * appears at the latest close, a trend line spans the recent past — so a single
 * click produces something meaningful that the user then drags into place,
 * instead of an empty shape at an arbitrary position.
 */

interface ChartPanelProps {
  position: PositionView | null;
}

/** Builds the initial geometry for a new drawing of the given type. */
const seedPayload = (
  type: DrawingType,
  context: { latestDate: string; latestClose: number; firstDate: string },
): DrawingPayload => {
  const { latestDate, latestClose } = context;
  /* Anchor the diagonal shapes roughly one quarter back, a useful default span. */
  const anchorDate = asIsoDate(addDays(latestDate, -90));

  switch (type) {
    case 'stop_loss':
      /* Der Kurs wird serverseitig auf den ausgewerteten Stop gesetzt. */
      return { type: 'stop_loss', price: latestClose, distanceLeg: true };
    case 'horizontal_line':
      /*
       * Etwas unterhalb des letzten Schlusses: Eine Unterstützung liegt der
       * Natur nach unter dem Kurs, und genau auf dem Schluss läge sie hinter der
       * Kerze. Fünf Prozent sind nah genug, um sie mit einem kurzen Zug auf das
       * gemeinte Niveau zu bringen.
       */
      return { type: 'horizontal_line', price: latestClose * 0.95 };
    case 'trend_line':
      return {
        type: 'trend_line',
        from: { date: anchorDate, price: latestClose * 0.9 },
        to: { date: asIsoDate(latestDate), price: latestClose },
        extendLeft: false,
        extendRight: true,
      };
    case 'channel':
      return {
        type: 'channel',
        from: { date: anchorDate, price: latestClose * 0.9 },
        to: { date: asIsoDate(latestDate), price: latestClose },
        offset: latestClose * 0.05,
        extendLeft: false,
        extendRight: true,
      };
  }
};

export const ChartPanel = ({ position }: ChartPanelProps): React.JSX.Element => {
  const instrumentId = position?.instrumentId ?? null;
  const barsQuery = useBars(instrumentId);
  const drawingsQuery = useDrawings(instrumentId);
  const [selectedDrawingId, setSelectedDrawingId] = useState<number | null>(null);

  const createDrawing = useCreateDrawing(instrumentId ?? 0);
  const updateDrawing = useUpdateDrawing(instrumentId ?? 0);
  const deleteDrawing = useDeleteDrawing(instrumentId ?? 0);
  const linkDrawing = useLinkDrawing(instrumentId ?? 0);
  const unlinkDrawing = useUnlinkDrawing(instrumentId ?? 0);
  const syncStopLoss = useSyncStopLoss(instrumentId ?? 0);

  const drawings = useMemo<Drawing[]>(() => drawingsQuery.data ?? [], [drawingsQuery.data]);

  /*
   * Der eingebuchte Stop als Ghost-Linie — aber nur, wenn er von der Linie
   * abweicht.
   *
   * Die Stop-Loss-Linie zeigt die **Absicht**, die Einbuchung die Order, die beim
   * Broker tatsächlich liegt. Solange beide übereinander liegen, gibt es nichts zu
   * zeigen; zwei Striche auf demselben Pixel sähen nur nach einer dickeren Linie
   * aus. Weichen sie ab, ist genau das die Information: Hier ist etwas verschoben
   * und noch nicht nachgeführt worden.
   *
   * Ein halber Cent als Schwelle — darunter fallen beide ohnehin auf dieselbe
   * Bildzeile.
   */
  const ghostStop = useMemo<number | null>(() => {
    const booked = position?.booking?.stopPrice ?? null;
    if (booked === null) return null;

    const linie = drawings.find((drawing) => drawing.payload.type === 'stop_loss');
    if (linie?.payload.type === 'stop_loss' && Math.abs(linie.payload.price - booked) < 0.005) {
      return null;
    }
    return booked;
  }, [position?.booking?.stopPrice, drawings]);

  /**
   * Der Stand des verbundenen Paares — dieselbe Rechnung wie im Nachtlauf.
   *
   * Gerechnet wird hier und nicht am Server, weil die Oberfläche alles dafür schon
   * hat: die Kerzen, die Zeichnungen und den eingebuchten Stop. Entscheidend ist,
   * dass es **dieselben** Funktionen aus `shared` sind, mit denen die E-Mail
   * geschrieben wird — eine zweite Rechnung liefe früher oder später auseinander,
   * und zwar unbemerkt, weil beide für sich plausibel aussähen.
   */
  const linkState = useMemo(() => {
    const verbunden = drawings.find((drawing) => drawing.linkedDrawingId != null);
    if (!verbunden) return null;

    const tage = (barsQuery.data?.bars ?? []).map((bar) => bar.date);
    const jüngster = tage.at(-1) ?? null;
    const booked = position?.booking?.stopPrice ?? null;
    const indikator = jüngster === null ? null : drawingPriceAt(verbunden.payload, jüngster, tage);
    const abstand = indikator === null || booked === null ? null : indikator - booked;

    return {
      verbunden,
      indikator,
      outcome: evaluateStopLossAlert(verbunden.referenceDistance, abstand),
      /* Wohin das Nachziehen die Linie setzen würde. */
      ziel:
        indikator === null || verbunden.referenceDistance === null
          ? null
          : indikator - verbunden.referenceDistance,
    };
  }, [drawings, barsQuery.data, position?.booking?.stopPrice]);

  /** A finished drag: persist the geometry, then release the local draft. */
  const handleGeometryCommitted = useCallback(
    (drawingId: number, payload: DrawingPayload) => {
      updateDrawing.mutate(
        { id: drawingId, input: { payload } },
        { onSettled: () => layerRef.current?.confirmSaved(drawingId) },
      );
    },
    [updateDrawing],
  );

  const { containerRef, layerRef, fitContent, showLastDays } = useChart({
    series: barsQuery.data,
    drawings,
    ghostStop,
    currency: position?.instrument.currency ?? null,
    onGeometryCommitted: handleGeometryCommitted,
    onSelectionChanged: setSelectedDrawingId,
    onDeleteRequested: (drawingId) => deleteDrawing.mutate(drawingId),
    /*
     * Doppelklick auf eine freie Stelle setzt den Stop-Loss auf diese Höhe:
     * Gibt es noch keine Linie, entsteht sie dort; sonst wandert die vorhandene
     * dorthin. Eine zweite anzulegen würde am Server scheitern und wäre als
     * Fehlermeldung die schlechtere Antwort auf eine eindeutige Geste.
     */
    onEmptyClick: (price) => {
      if (instrumentId === null) return;

      const existing = drawings.find((drawing) => drawing.payload.type === 'stop_loss');
      if (existing && existing.payload.type === 'stop_loss') {
        updateDrawing.mutate({
          id: existing.id,
          input: { payload: { ...existing.payload, price } },
        });
        return;
      }

      const input: CreateDrawingInput = {
        label: DRAWING_TYPE_LABELS.stop_loss,
        showPrice: true,
        payload: { type: 'stop_loss', price, distanceLeg: true },
      };
      createDrawing.mutate(input);
    },
  });

  /* Je Chart ist nur eine Stop-Loss-Linie zulässig. */

  const addDrawing = (type: DrawingType): void => {
    const bars = barsQuery.data?.bars ?? [];
    const latest = bars.at(-1);
    const first = bars[0];
    if (!latest || !first || instrumentId === null) return;

    createDrawing.mutate({
      label: DRAWING_TYPE_LABELS[type],
      showPrice: true,
      payload: seedPayload(type, {
        latestDate: latest.date,
        latestClose: latest.close,
        firstDate: first.date,
      }),
    });
  };

  if (!position) {
    return (
      <section className="panel chart-panel chart-panel--empty">
        <p className="muted">Wähle links einen Wert aus, um seinen Chart zu sehen.</p>
      </section>
    );
  }

  const latestBar = barsQuery.data?.bars.at(-1);

  return (
    <section className="panel chart-panel">
      <header className="chart-panel__header">
        <div>
          <h2>
            {position.instrument.symbol}
            <span className="muted"> · {position.instrument.name}</span>
          </h2>
          {latestBar ? (
            <p className="muted small">
              Letzter Kurs{' '}
              {formatPrice(latestBar.close, { currency: position.instrument.currency })} vom{' '}
              {latestBar.date} · {barsQuery.data?.bars.length ?? 0} Tage geladen
            </p>
          ) : (
            <p className="muted small">Keine Kursdaten — bitte Daten aktualisieren.</p>
          )}
        </div>

        <div className="chart-panel__tools">
          <div className="button-group" role="group" aria-label="Zoom">
            <button type="button" onClick={() => showLastDays(60)}>
              3M
            </button>
            <button type="button" onClick={() => showLastDays(250)}>
              1J
            </button>
            <button type="button" onClick={() => showLastDays(1250)}>
              5J
            </button>
            <button type="button" onClick={fitContent}>
              Alles
            </button>
          </div>

          {/*
            Die Liste der Indikatoren liegt jetzt hinter diesem Knopf statt
            dauerhaft unter dem Chart. Sie wird gebraucht, wenn man etwas
            umbenennt, abschwächt oder löscht — nicht beim Betrachten, und genau
            dort nahm sie zuvor Höhe weg.
          */}
          <Popover
            label={`Indikatoren${drawings.length > 0 ? ` (${drawings.length})` : ''}`}
            title="Vorhandene Indikatoren bearbeiten"
          >
            <div className="popover__head">
              <strong>Indikatoren</strong>
              <p className="muted small">
                Ziehen verschiebt einen Indikator, die Punkte an den Enden verformen ihn.
                Beschriftungen erscheinen beim Überfahren; die Stop-Loss-Linie ist immer
                beschriftet. <kbd>Entf</kbd> löscht den ausgewählten, <kbd>Esc</kbd> bricht das
                Ziehen ab. Wird die Stop-Loss-Linie verschoben, übernimmt die Position den neuen
                Kurs als festen Stop.
              </p>
            </div>

            {/*
              Der Zustand des verbundenen Paares, und gleich daneben die Handlung.
              Hier und nicht in der Zeile des Indikators: Die Zahl gilt dem Paar,
              nicht einer der beiden Linien, und in der Liste stünde sie an einer
              von zwei Stellen, an denen sie nur halb hingehört.
            */}
            {linkState ? (
              <LinkState
                drawing={linkState.verbunden}
                outcome={linkState.outcome}
                target={linkState.ziel}
                currency={position.instrument.currency}
                onSync={() => syncStopLoss.mutate()}
                isPending={syncStopLoss.isPending}
                outdatedBookingPrice={
                  syncStopLoss.data?.bookingOutdated ? syncStopLoss.data.newPrice : null
                }
                errorMessage={syncStopLoss.isError ? syncStopLoss.error.message : null}
              />
            ) : null}

            <DrawingList
              drawings={drawings}
              selectedId={selectedDrawingId}
              currency={position.instrument.currency}
              onSelect={setSelectedDrawingId}
              onUpdate={(id, input) => updateDrawing.mutate({ id, input })}
              onDelete={(id) => deleteDrawing.mutate(id)}
              onLink={(id) => linkDrawing.mutate(id)}
              onUnlink={(id) => unlinkDrawing.mutate(id)}
            />
          </Popover>

          {/*
            Ohne die Stop-Loss-Linie: Sie entsteht mit der Position und lässt sich
            weder anlegen noch entfernen. Ein Knopf dafür wäre eine Aufforderung
            zu etwas, das immer schon geschehen ist.
          */}
          <div className="button-group" role="group" aria-label="Indikator hinzufügen">
            {(Object.keys(DRAWING_TYPE_LABELS) as DrawingType[])
              .filter((type) => type !== 'stop_loss')
              .map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => addDrawing(type)}
                  disabled={!latestBar || createDrawing.isPending}
                  title={`${DRAWING_TYPE_LABELS[type]} hinzufügen`}
                >
                  + {DRAWING_TYPE_LABELS[type]}
                </button>
              ))}
          </div>
        </div>
      </header>

      <div className="chart-panel__canvas" ref={containerRef} />
    </section>
  );
};
